import React from 'react';
import { ReactComponent as InfoSolidCircle } from '@assets/icons/info-solid-circle.svg';

import {
  Box,
  BoxProps,
  Flex,
  Grid,
  GridItem,
  Tooltip,
  useTheme,
  VStack
} from '@chakra-ui/react';

import { Divider, Text } from 'ui';

interface ShopCartFooterProps extends BoxProps {
  subTotal: string;
  tax: string;
  shippingCost: string | null;
  total: string;
  isShipmentExist?: boolean;
  isShowTaxes: boolean;
}

const ShopCartFooter = ({
  subTotal,
  tax,
  shippingCost,
  total,
  isShowTaxes,
  isShipmentExist,
  ...props
}: ShopCartFooterProps) => {
  const {
    colors: {
      palette: {
        shop: {
          cart: { detailsStep: styles }
        }
      }
    }
  } = useTheme();

  return (
    <Box {...props}>
      {(isShipmentExist || isShowTaxes) && (
        <>
          <VStack gap='7px' py={{ base: '14px', md: '27px' }}>
            <Grid
              templateColumns='repeat(12, 1fr)'
              columnGap={{ base: 0, lg: '10px' }}
              display={{ base: 'flex', lg: 'grid' }}
              w='full'
            >
              <GridItem colSpan={6} />
              <GridItem colSpan={3} width='full' alignContent='center'>
                <Text
                  fontSize={{ base: '18px', md: '20px' }}
                  textTransform='uppercase'
                  color={styles.subtotalText}
                  textAlign={{ base: 'start', lg: 'right' }}
                >
                  Subtotal
                </Text>
              </GridItem>

              <GridItem colSpan={3}>
                <Text
                  fontSize={{ base: '28px', lg: '32px' }}
                  textTransform='uppercase'
                  color={styles.subtotalText}
                  textAlign='end'
                >
                  {subTotal}
                </Text>
              </GridItem>
            </Grid>

            {isShowTaxes && (
              <Grid
                templateColumns='repeat(12, 1fr)'
                columnGap={{ base: 0, lg: '10px' }}
                display={{ base: 'flex', lg: 'grid' }}
                w='full'
                mb={shippingCost ? 0 : 2}
              >
                <GridItem colSpan={8} />

                <>
                  <GridItem colSpan={1} width='full' alignContent='center'>
                    <Flex
                      justifyContent={{ base: 'flex-start', lg: 'flex-end' }}
                      alignItems='center'
                      w='full'
                    >
                      <Text
                        fontSize={{ base: '18px', md: '20px' }}
                        textTransform='uppercase'
                        color={styles.taxText}
                        textAlign={{ base: 'start', lg: 'right' }}
                        mr={1}
                      >
                        Tax
                      </Text>
                      <Tooltip
                        label='Sales tax is calculated based on your shipping address'
                        placement='bottom-start'
                        shouldWrapChildren
                        py='15px'
                        px='12px'
                        width='210px'
                        bg='black'
                        color='white'
                        borderRadius='2px'
                        top={-1}
                        left={2}
                      >
                        <InfoSolidCircle width={18} height={18} />
                      </Tooltip>
                    </Flex>
                  </GridItem>
                  <GridItem colSpan={1} />
                  <GridItem colSpan={2} w='full'>
                    <Text
                      fontSize={['20px', '23px', '25px']}
                      textTransform='uppercase'
                      color={styles.taxText}
                      textAlign='right'
                    >
                      {tax}
                    </Text>
                  </GridItem>
                </>
              </Grid>
            )}

            {isShipmentExist && (
              <Grid
                templateColumns='repeat(12, 1fr)'
                columnGap={{ base: 0, lg: '10px' }}
                display={{ base: 'flex', lg: 'grid' }}
                w='full'
              >
                <GridItem colSpan={8} />
                <GridItem colSpan={1} width='full' alignContent='center'>
                  <Text
                    fontSize={{ base: '18px', md: '20px' }}
                    textTransform='uppercase'
                    color={styles.taxText}
                    textAlign={{ base: 'start', lg: 'right' }}
                  >
                    Shipping
                  </Text>
                </GridItem>
                <GridItem colSpan={1} />

                <GridItem colSpan={2} alignContent='center' width='full'>
                  {shippingCost ? (
                    <Text
                      fontSize={['20px', '23px', '25px']}
                      textTransform='uppercase'
                      color={styles.taxText}
                      textAlign='right'
                    >
                      {shippingCost}
                    </Text>
                  ) : (
                    <Text
                      fontSize={['12px', '14px']}
                      textTransform='uppercase'
                      color={styles.footerSmallText}
                      textAlign='right'
                    >
                      Calculated on next step
                    </Text>
                  )}
                </GridItem>
              </Grid>
            )}
          </VStack>
          <Divider bg={styles.divider} height='2px' border='none' />
        </>
      )}
      <VStack gap={0}>
        <Grid
          templateColumns='repeat(12, 1fr)'
          columnGap={{ base: 0, lg: '10px' }}
          display={{ base: 'flex', lg: 'grid' }}
          w='full'
          py={{ base: '12px', md: '22px' }}
        >
          <GridItem colSpan={8} />
          <GridItem colSpan={1} width='full' alignContent='center'>
            <Text
              fontSize={{ base: '18px', md: '20px' }}
              textTransform='uppercase'
              color={styles.taxText}
              textAlign={{ base: 'start', lg: 'right' }}
            >
              Total
            </Text>
          </GridItem>

          <GridItem colSpan={3} alignContent='center'>
            <Text
              fontSize={{ base: '38px', lg: '42px' }}
              textTransform='uppercase'
              color={styles.taxText}
              textAlign='right'
            >
              {total}
            </Text>
          </GridItem>
        </Grid>

        <Divider bg={styles.divider} height='1px' border='none' />
      </VStack>
    </Box>
  );
};

export default ShopCartFooter;
